import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, UserX, Globe, ClipboardList, Users, MapPin, Building2, ChevronDown, Network, ExternalLink, UserCheck, Trash2, PowerOff, Power } from 'lucide-react';
import { toast } from 'sonner';
import { useCrud } from '../../api/hooks/useCrud';
import { api } from '../../api/client';
import { DataTable } from '../../components/ui/DataTable';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal, PasswordConfirmModal } from '../../components/ui';
import { UserManagerTab } from '../../components/gestao/UserManagerTab';
import { useDevStore } from '../../store/useDevStore';
import '../../components/ui/datatable.css';
import { ROUTES } from '../../api/routes';

interface Rede { id: string; nome: string; descricao?: string; ativo?: boolean; }
interface Estado {
  id: string; nome: string; sigla: string; redeId?: string; ativo: boolean;
  rede?: { id: string; nome: string; descricao?: string };
  _count?: { municipios: number; usuarios: number; gestores?: number };
  _deepCount?: { escolas: number };
  criadoEm: string;
}

interface RelMunicipio { id: string; nome: string; codigoIBGE?: string; ativo: boolean; _count?: { escolas: number } }
interface RelEscola { id: string; nome: string; codigo: string; tipoRede: string; cidade?: string; ativo: boolean; _count?: { alunos: number; turmas: number } }

export const ESTADOS_BRASIL_CANONICOS = [
  { nome: 'Acre', sigla: 'AC' },
  { nome: 'Alagoas', sigla: 'AL' },
  { nome: 'Amapá', sigla: 'AP' },
  { nome: 'Amazonas', sigla: 'AM' },
  { nome: 'Bahia', sigla: 'BA' },
  { nome: 'Ceará', sigla: 'CE' },
  { nome: 'Distrito Federal', sigla: 'DF' },
  { nome: 'Espírito Santo', sigla: 'ES' },
  { nome: 'Goiás', sigla: 'GO' },
  { nome: 'Maranhão', sigla: 'MA' },
  { nome: 'Mato Grosso', sigla: 'MT' },
  { nome: 'Mato Grosso do Sul', sigla: 'MS' },
  { nome: 'Minas Gerais', sigla: 'MG' },
  { nome: 'Pará', sigla: 'PA' },
  { nome: 'Paraíba', sigla: 'PB' },
  { nome: 'Paraná', sigla: 'PR' },
  { nome: 'Pernambuco', sigla: 'PE' },
  { nome: 'Piauí', sigla: 'PI' },
  { nome: 'Rio de Janeiro', sigla: 'RJ' },
  { nome: 'Rio Grande do Norte', sigla: 'RN' },
  { nome: 'Rio Grande do Sul', sigla: 'RS' },
  { nome: 'Rondônia', sigla: 'RO' },
  { nome: 'Roraima', sigla: 'RR' },
  { nome: 'Santa Catarina', sigla: 'SC' },
  { nome: 'São Paulo', sigla: 'SP' },
  { nome: 'Sergipe', sigla: 'SE' },
  { nome: 'Tocantins', sigla: 'TO' },
];

export function EstadosPage() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { data, isLoading, create, update, deactivate, refetch } = useCrud<Estado>('admin/estados');
  const devSettings = useDevStore((s) => s.settings);
  const [redes, setRedes] = useState<Rede[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Estado | null>(null);
  const [editTab, setEditTab] = useState<'dados' | 'municipios' | 'escolas' | 'gestores'>('dados');
  const [modoPais, setModoPais] = useState<'brasil' | 'outro'>('brasil');
  const [form, setForm] = useState({ nome: '', sigla: '', redeId: '' });
  const [saving, setSaving] = useState(false);
  const [selectedUserForModal, setSelectedUserForModal] = useState<any | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Estado | null>(null);
  const [estadoToHardDelete, setEstadoToHardDelete] = useState<Estado | null>(null);

  // Related entities
  const [relMunicipios, setRelMunicipios] = useState<RelMunicipio[]>([]);
  const [relEscolas, setRelEscolas] = useState<RelEscola[]>([]);
  const [relLoading, setRelLoading] = useState(false);

  // Entidades globais para agregações completas de ativos/inativos
  const [allRedesGlobal, setAllRedesGlobal] = useState<Rede[]>([]);
  const [allMunicipiosGlobal, setAllMunicipiosGlobal] = useState<any[]>([]);
  const [allEscolasGlobal, setAllEscolasGlobal] = useState<any[]>([]);

  useEffect(() => {
    api.get(ROUTES.admin.redes).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
      setAllRedesGlobal(list);
      const activeRedes = list.filter((x: any) => x.ativo !== false);
      activeRedes.sort((a: any, b: any) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
      setRedes(activeRedes);
      if (activeRedes.length === 1) {
        setForm(prev => ({ ...prev, redeId: activeRedes[0].id }));
      }
    }).catch(() => {});

    api.get(ROUTES.admin.municipios).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
      setAllMunicipiosGlobal(list);
    }).catch(() => {});

    api.get(ROUTES.escolas.listar).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
      setAllEscolasGlobal(list);
    }).catch(() => {});
  }, []);

  // Auto-seleciona a rede se houver apenas 1 cadastrada
  useEffect(() => {
    if (redes.length === 1 && !form.redeId) {
      setForm(prev => ({ ...prev, redeId: redes[0].id }));
    }
  }, [redes, form.redeId]);

  // Fetch related data when editing & switching tabs
  useEffect(() => {
    if (!editing) return;
    if (editTab === 'municipios' && relMunicipios.length === 0) {
      setRelLoading(true);
      api.get(ROUTES.admin.municipiosPorEstado(editing.id)).then(r => {
        const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
        setRelMunicipios(list);
      }).catch(() => {}).finally(() => setRelLoading(false));
    }
    if (editTab === 'escolas' && relEscolas.length === 0) {
      setRelLoading(true);
      api.get(ROUTES.escolas.listar).then(r => {
        const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
        setRelEscolas(list.filter((e: any) => e.municipio?.estado?.id === editing.id));
      }).catch(() => {}).finally(() => setRelLoading(false));
    }
  }, [editing, editTab]);

  const openNew = () => {
    setEditing(null);
    setModoPais('brasil');
    const redePublica = redes.find(r => r.nome.toLowerCase().includes('publica') || r.nome.toLowerCase().includes('pública'));
    setForm({
      nome: 'Minas Gerais',
      sigla: 'MG',
      redeId: redePublica?.id || redes[0]?.id || ''
    });
    setModalOpen(true);
  };

  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const hasFormData = () => {
    if (editing) return false;
    return !!(form.nome.trim() || form.sigla.trim());
  };

  const handleCancelClick = () => {
    if (hasFormData()) {
      setDiscardConfirmOpen(true);
    } else {
      toast.dismiss();
      setModalOpen(false);
    }
  };

  const handleConfirmDiscard = () => {
    setDiscardConfirmOpen(false);
    toast.dismiss();
    setModalOpen(false);
  };
  const openEdit = (e: Estado) => {
    setEditing(e); setEditTab('dados'); setRelMunicipios([]); setRelEscolas([]);
    setForm({ nome: e.nome, sigla: e.sigla, redeId: e.redeId || e.rede?.id || '' });
    setModalOpen(true);
  };

  useEffect(() => {
    if (loc.state?.openNew && !modalOpen && redes.length > 0) {
      openNew();
      navigate(loc.pathname, { replace: true, state: {} });
    }
    if (loc.state?.editId && data && !modalOpen) {
      const found = (data as Estado[]).find((e: Estado) => e.id === loc.state.editId);
      if (found) { openEdit(found); navigate(loc.pathname, { replace: true, state: {} }); }
    }
  }, [loc.state, redes, data, modalOpen, navigate]);

  const handleSave = async () => {
    if (!form.nome.trim() || !form.sigla.trim()) { toast.error('Nome e Sigla são obrigatórios'); return; }
    if (!form.redeId) { toast.error('Selecione uma Rede'); return; }
    if (form.sigla.length !== 2) { toast.error('Sigla deve ter 2 caracteres'); return; }
    setSaving(true);
    try {
      if (editing) { await update.mutateAsync({ id: editing.id, nome: form.nome, sigla: form.sigla } as any); toast.success('Estado atualizado!'); }
      else { await create.mutateAsync(form as any); toast.success('Estado criado!'); }
      setModalOpen(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const handleActivateEstado = async (e: Estado) => {
    try {
      await update.mutateAsync({ id: e.id, ativo: true } as any);
      toast.success('Estado ativado com sucesso!');
      refetch();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erro ao ativar estado');
    }
  };

  const handleHardDeleteEstado = (e: Estado) => {
    setEstadoToHardDelete(e);
  };

  const confirmHardDeleteEstado = async () => {
    if (!estadoToHardDelete) return;
    const e = estadoToHardDelete;
    setEstadoToHardDelete(null);
    try {
      await api.delete(`/admin/estados/hard/${e.id}`);
      toast.success('Estado removido com sucesso!');
      refetch();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erro ao remover estado permanentemente');
    }
  };

  const requestDelete = (e: Estado) => {
    const mun = e._count?.municipios || 0;
    const esc = e._deepCount?.escolas || 0;
    if (mun > 0 || esc > 0) {
      const detalhes: string[] = [];
      if (mun > 0) detalhes.push(`${mun} município${mun !== 1 ? 's' : ''}`);
      if (esc > 0) detalhes.push(`${esc} escola${esc !== 1 ? 's' : ''}`);
      toast.warning(`Não é possível desativar o estado "${e.nome}". Ele possui ${detalhes.join(' e ')} vinculados.`);
      return;
    }
    setDeleteConfirm(e);
  };
  const confirmDeleteEstado = async () => {
    if (!deleteConfirm) return;
    const id = deleteConfirm.id;
    setDeleteConfirm(null);
    try {
      await deactivate.mutateAsync(id);
      toast.success('Estado desativado com sucesso');
      refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.error?.message || e?.response?.data?.message || 'Não foi possível desativar o estado';
      toast.warning(msg);
    }
  };

  // ── Tabs ───────────────────────────────────────────────────
  const TABS = [
    { key: 'dados' as const, icon: <ClipboardList size={14} />, label: 'Dados' },
    { key: 'municipios' as const, icon: <MapPin size={14} />, label: 'Municípios' },
    { key: 'escolas' as const, icon: <Building2 size={14} />, label: 'Escolas' },
    { key: 'gestores' as const, icon: <Users size={14} />, label: 'Gestores' },
  ];

  const renderEditTabs = () => editing && (
    <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border, #e5e7eb)', marginBottom: 16 }}>
      {TABS.map(t => (
        <button key={t.key} onClick={() => setEditTab(t.key)} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
          background: 'none', border: 'none', cursor: 'pointer',
          fontWeight: editTab === t.key ? 700 : 500, fontSize: '.83rem',
          color: editTab === t.key ? '#6366f1' : '#6b7280',
          borderBottom: editTab === t.key ? '2px solid #6366f1' : '2px solid transparent',
          marginBottom: -2, transition: 'all .15s',
        }}>{t.icon} {t.label}</button>
      ))}
    </div>
  );

  const renderDadosForm = () => (
    <>
      {/* ── Banner de Bloqueio se não houver Redes ── */}
      {!editing && redes.length === 0 && (
        <div style={{
          padding: '12px 14px',
          borderRadius: 8,
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          marginBottom: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}>
          <div style={{ color: '#ef4444', fontWeight: 700, fontSize: '.85rem' }}>
            ⚠️ Nenhuma Rede Mantenedora Cadastrada
          </div>
          <div style={{ color: '#64748b', fontSize: '.8rem', lineHeight: 1.4 }}>
            É obrigatório possuir ao menos uma Rede de Ensino no sistema para vincular o Estado.
          </div>
          <button
            type="button"
            onClick={() => { setModalOpen(false); navigate('/master/redes'); }}
            style={{
              alignSelf: 'flex-start',
              padding: '6px 12px',
              borderRadius: 6,
              background: '#ef4444',
              color: '#fff',
              border: 'none',
              fontSize: '.75rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            + Cadastrar Nova Rede
          </button>
        </div>
      )}

      {!editing && redes.length > 0 && (
        <div className="form-group">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <label className="form-label" style={{ fontWeight: 700, margin: 0 }}>Rede Mantenedora *</label>
            {redes.find(r => r.id === form.redeId)?.descricao && (
              <span style={{ fontSize: '.72rem', color: '#6366f1', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span>ℹ️</span> <span>Premissa da Rede</span>
              </span>
            )}
          </div>

          <select
            className="form-select"
            name="redeId"
            value={form.redeId}
            onChange={e => setForm({ ...form, redeId: e.target.value })}
          >
            <option value="">Selecione a rede...</option>
            {redes.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
          </select>

          {/* ── Box de Orientação / Explicação da Rede Selecionada ── */}
          {(() => {
            const selectedRede = redes.find(r => r.id === form.redeId);
            if (!selectedRede) return null;
            return (
              <div style={{
                marginTop: 6,
                padding: '8px 12px',
                borderRadius: 8,
                background: 'rgba(99, 102, 241, 0.06)',
                border: '1px solid rgba(99, 102, 241, 0.18)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                fontSize: '.78rem',
                color: '#334155',
                lineHeight: 1.4,
              }}>
                <span style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>🏛️</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: '#4f46e5', marginBottom: 2 }}>
                    {selectedRede.nome}
                  </div>
                  <div>
                    {selectedRede.descricao
                      ? selectedRede.descricao
                      : 'Rede mantenedora institucional vinculada para centralização e gestão de estados, municípios e polos escolares.'}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Mensagem orientativa de Rede não selecionada ── */}
      {!editing && !form.redeId && redes.length > 0 && (
        <div style={{
          fontSize: '.78rem',
          color: '#1e40af',
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 8,
          padding: '9px 14px',
          marginBottom: 12,
          marginTop: -4,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontWeight: 600,
        }}>
          <span style={{ fontSize: '1rem' }}>ℹ️</span>
          <span>Selecione a Rede Mantenedora acima para liberar a configuração do Estado.</span>
        </div>
      )}

      {/* ── Bloco de Configuração Territorial do Estado (Bloqueado com opacidade 0.5 enquanto não houver Rede selecionada) ── */}
      <div style={{
        opacity: (editing || form.redeId) ? 1 : 0.5,
        pointerEvents: (editing || form.redeId) ? 'auto' : 'none',
        transition: 'opacity 0.2s ease',
      }}>
        {/* ── Seletor de Padrão Territorial (Brasil vs Outro País) ── */}
        {!editing && (
          <div style={{ marginBottom: 12 }}>
            <label className="form-label" style={{ fontWeight: 700, marginBottom: 6 }}>
              Padrão Territorial *
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => {
                  setModoPais('brasil');
                  setForm(prev => ({ ...prev, nome: 'Minas Gerais', sigla: 'MG' }));
                }}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: 8,
                  fontSize: '.8rem',
                  fontWeight: 700,
                  cursor: (editing || form.redeId) ? 'pointer' : 'not-allowed',
                  border: modoPais === 'brasil' ? '2px solid #6366f1' : '1px solid var(--border, #e5e7eb)',
                  background: modoPais === 'brasil' ? 'rgba(99, 102, 241, 0.1)' : 'var(--bg-muted, #f8fafc)',
                  color: modoPais === 'brasil' ? '#4f46e5' : '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  transition: 'all 0.15s',
                }}
              >
                <span>🇧🇷</span>
                <span>Brasil (27 Estados IBGE)</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setModoPais('outro');
                  setForm(prev => ({ ...prev, nome: '', sigla: '' }));
                }}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: 8,
                  fontSize: '.8rem',
                  fontWeight: 700,
                  cursor: (editing || form.redeId) ? 'pointer' : 'not-allowed',
                  border: modoPais === 'outro' ? '2px solid #6366f1' : '1px solid var(--border, #e5e7eb)',
                  background: modoPais === 'outro' ? 'rgba(99, 102, 241, 0.1)' : 'var(--bg-muted, #f8fafc)',
                  color: modoPais === 'outro' ? '#4f46e5' : '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  transition: 'all 0.15s',
                }}
              >
                <span>🌍</span>
                <span>Outro País / Personalizado</span>
              </button>
            </div>
          </div>
        )}

        {/* ── Seleção dos 27 Estados do Brasil ── */}
        {!editing && modoPais === 'brasil' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontWeight: 700 }}>
                Selecione o Estado Oficial (27 UFs) *
              </label>
              <select
                className="form-select"
                name="estadoPadrao"
                value={form.sigla}
                disabled={!editing && !form.redeId}
                onChange={e => {
                  const found = ESTADOS_BRASIL_CANONICOS.find(est => est.sigla === e.target.value);
                  if (found) {
                    setForm(prev => ({ ...prev, nome: found.nome, sigla: found.sigla }));
                  }
                }}
              >
                {ESTADOS_BRASIL_CANONICOS.map(est => (
                  <option key={est.sigla} value={est.sigla}>
                    {est.nome} ({est.sigla})
                  </option>
                ))}
              </select>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              gap: 12,
              background: 'var(--bg-muted, #f8fafc)',
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid var(--border, #e2e8f0)',
            }}>
              <div>
                <div style={{ fontSize: '.72rem', color: '#64748b', fontWeight: 600 }}>Nome Registrado:</div>
                <div style={{ fontSize: '.88rem', fontWeight: 700, color: '#0f172a' }}>{form.nome || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: '.72rem', color: '#64748b', fontWeight: 600 }}>Sigla UF:</div>
                <div style={{ fontSize: '.88rem', fontWeight: 700, color: '#4f46e5' }}>{form.sigla || '—'}</div>
              </div>
            </div>
          </div>
        )}

        {/* ── Modo Outro País ou Edição (Campos Livres) ── */}
        {(editing || modoPais === 'outro') && (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Nome do Estado / Província *</label>
              <input
                className="form-input"
                name="nome"
                value={form.nome}
                disabled={!editing && !form.redeId}
                onChange={e => setForm({ ...form, nome: e.target.value })}
                placeholder="Ex: Minas Gerais ou Califórnia"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Sigla *</label>
              <input
                className="form-input"
                name="sigla"
                value={form.sigla}
                disabled={!editing && !form.redeId}
                onChange={e => setForm({ ...form, sigla: e.target.value.toUpperCase() })}
                maxLength={2}
                placeholder="Ex: MG ou CA"
              />
            </div>
          </div>
        )}
      </div>
    </>
  );

  const tipoRedeColors: Record<string, string> = { MUNICIPAL: '#10b981', ESTADUAL: '#6366f1', PARTICULAR: '#f59e0b' };

  const renderMunicipiosList = () => {
    if (relLoading) return <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>Carregando...</div>;
    if (relMunicipios.length === 0) return (
      <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>
        <MapPin size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
        <p style={{ margin: 0 }}>Nenhum município vinculado a este estado</p>
      </div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: '.85rem', color: '#6b7280', marginBottom: 6 }}>{relMunicipios.length} município(s)</div>
        {relMunicipios.map(m => (
          <div key={m.id} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
            borderRadius: 8, border: '1px solid var(--border, #e5e7eb)', background: 'var(--bg-muted, #f9fafb)',
          }}>
            <MapPin size={16} style={{ color: '#f59e0b', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '.88rem' }}>{m.nome}</div>
              {m.codigoIBGE && <div style={{ fontSize: '.72rem', color: '#6b7280' }}>IBGE: {m.codigoIBGE}</div>}
            </div>
            <span className="badge badge-active">{m._count?.escolas || 0} escolas</span>
            <span className={`badge ${m.ativo ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: '.65rem' }}>{m.ativo ? 'Ativo' : 'Inativo'}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderEscolasList = () => {
    if (relLoading) return <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>Carregando...</div>;
    if (relEscolas.length === 0) return (
      <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>
        <Building2 size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
        <p style={{ margin: 0 }}>Nenhuma escola vinculada a este estado</p>
      </div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: '.85rem', color: '#6b7280', marginBottom: 6 }}>{relEscolas.length} escola(s)</div>
        {relEscolas.map(e => {
          const color = tipoRedeColors[e.tipoRede] || '#6b7280';
          return (
            <div key={e.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              borderRadius: 8, border: '1px solid var(--border, #e5e7eb)', background: 'var(--bg-muted, #f9fafb)',
            }}>
              <Building2 size={16} style={{ color: '#3b82f6', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.nome}</div>
                <div style={{ fontSize: '.72rem', color: '#6b7280' }}>{e.codigo} · {e.cidade}</div>
              </div>
              <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '.65rem', fontWeight: 600, background: `${color}18`, color }}>{e.tipoRede}</span>
              <span className="badge badge-active" style={{ fontSize: '.65rem' }}>{e._count?.alunos || 0} alunos</span>
            </div>
          );
        })}
      </div>
    );
  };

  const isFormValid = useMemo(() => {
    if (!form.nome.trim() || !form.sigla.trim() || !form.redeId) return false;
    if (form.sigla.trim().length !== 2) return false;
    return true;
  }, [form]);

  const renderFooter = () => {
    if (editing && editTab !== 'dados') return <button className="btn-cancel" onClick={() => setModalOpen(false)}>Fechar</button>;
    return (
      <>
        <button className="btn-cancel" onClick={editing ? () => setModalOpen(false) : handleCancelClick}>
          Cancelar
        </button>
        <button
          className="btn-save"
          onClick={handleSave}
          disabled={saving || !isFormValid}
          style={{
            opacity: isFormValid ? 1 : 0.5,
            cursor: isFormValid ? (saving ? 'wait' : 'pointer') : 'not-allowed',
            transition: 'all 0.2s ease',
          }}
          title={!isFormValid ? 'Preencha Nome, Sigla (2 letras) e selecione a Rede' : undefined}
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </button>
      </>
    );
  };

  const renderEditContent = () => {
    if (!editing) return null;
    switch (editTab) {
      case 'dados': return renderDadosForm();
      case 'municipios': return renderMunicipiosList();
      case 'escolas': return renderEscolasList();
      case 'gestores': return <UserManagerTab entityType="estado" entityId={editing.id} label="Gestores Estaduais" defaultPerfil="ESTADUAL" />;
    }
  };

  // ── Agrupamento por Rede ─────────────────────────────
  const [search, setSearch] = useState('');
  const [collapsedRedes, setCollapsedRedes] = useState<Set<string>>(new Set());

  const toggleRede = (redeId: string) => {
    const next = new Set(collapsedRedes);
    next.has(redeId) ? next.delete(redeId) : next.add(redeId);
    setCollapsedRedes(next);
  };

  const groupedByRede = useMemo(() => {
    const filtered = search.trim()
      ? data.filter((e: Estado) => e.nome.toLowerCase().includes(search.toLowerCase()) || e.sigla.toLowerCase().includes(search.toLowerCase()))
      : data;

    const groups: Record<string, { rede: { id: string; nome: string } | null; items: Estado[] }> = {};
    filtered.forEach((e: Estado) => {
      const rede = e.rede;
      const key = rede?.id || '__sem_rede__';
      if (!groups[key]) groups[key] = { rede: rede || null, items: [] };
      groups[key].items.push(e);
    });

    return Object.values(groups)
      .sort((a, b) => {
        if (!a.rede) return 1;
        if (!b.rede) return -1;
        return a.rede.nome.localeCompare(b.rede.nome);
      })
      .map(group => ({
        ...group,
        items: [...group.items].sort((a, b) => {
          // Ativos primeiro, Inativos no final!
          if (a.ativo !== b.ativo) return a.ativo ? -1 : 1;
          return a.nome.localeCompare(b.nome);
        }),
      }));
  }, [data, search]);

  /* ── KPIs e Agregações Únicas ── */
  const stats = useMemo(() => {
    const list = ((data || []) as Estado[]);
    
    // 1. Estados únicos ativos e inativos
    const estadosAtivosSiglas = new Set<string>();
    list.forEach((e: Estado) => {
      const siglaNorm = (e.sigla || e.nome || '').trim().toUpperCase();
      if (!siglaNorm) return;
      if (e.ativo) {
        estadosAtivosSiglas.add(siglaNorm);
      }
    });
    const estadosAtivos = estadosAtivosSiglas.size;
    const estadosInativos = list.filter(e => !e.ativo).length;

    // 2. Municípios únicos ativos, em operação e inativos
    let municipiosAtivos = 0;
    let municipiosEmOperacao = 0;
    let municipiosPendentes = 0;
    let municipiosInativos = 0;
    if (allMunicipiosGlobal.length > 0) {
      const munAtivosSet = new Set<string>();
      const munEmOperacaoSet = new Set<string>();
      allMunicipiosGlobal.forEach((m: any) => {
        if (m.ativo) {
          const estadoSigla = (m.estado?.sigla || m.estado?.nome || m.estadoId || '').trim().toUpperCase();
          const nomeNorm = (m.nome || '').trim().toUpperCase();
          const chave = `${nomeNorm}__${estadoSigla}`;
          munAtivosSet.add(chave);
          if ((m._count?.escolas || 0) > 0) {
            munEmOperacaoSet.add(chave);
          }
        }
      });
      municipiosAtivos = munAtivosSet.size;
      municipiosEmOperacao = munEmOperacaoSet.size;
      municipiosPendentes = Math.max(0, municipiosAtivos - municipiosEmOperacao);
      municipiosInativos = allMunicipiosGlobal.filter((m: any) => !m.ativo).length;
    } else {
      list.filter(e => e.ativo).forEach(e => {
        municipiosAtivos += e._count?.municipios || 0;
      });
      municipiosEmOperacao = municipiosAtivos;
    }

    // 3. Escolas ativas, em atividade, em implantação e inativas
    let escolasAtivas = 0;
    let escolasEmAtividade = 0;
    let escolasEmImplantacao = 0;
    let escolasInativas = 0;
    if (allEscolasGlobal.length > 0) {
      const ativas = allEscolasGlobal.filter((esc: any) => esc.ativo);
      escolasAtivas = ativas.length;
      escolasInativas = allEscolasGlobal.filter((esc: any) => !esc.ativo).length;
      escolasEmAtividade = ativas.filter((e: any) => e.wizardConcluido || (Number(e.wizardEtapaAtual || 0) >= 7 && (e._count?.turmas || 0) > 0 && (e._count?.alunos || 0) > 0)).length;
      escolasEmImplantacao = Math.max(0, escolasAtivas - escolasEmAtividade);
    } else {
      list.filter(e => e.ativo).forEach(e => {
        escolasAtivas += e._deepCount?.escolas || 0;
      });
      escolasEmAtividade = escolasAtivas;
    }

    // 4. Redes ativas e inativas
    const redesAtivas = allRedesGlobal.length > 0
      ? allRedesGlobal.filter((r: any) => r.ativo).length
      : redes.length;
    const redesInativas = allRedesGlobal.filter((r: any) => !r.ativo).length;

    return {
      estadosAtivos,
      estadosInativos,
      municipiosAtivos,
      municipiosEmOperacao,
      municipiosPendentes,
      municipiosInativos,
      escolasAtivas,
      escolasEmAtividade,
      escolasEmImplantacao,
      escolasInativas,
      redesAtivas,
      redesInativas,
    };
  }, [data, allMunicipiosGlobal, allEscolasGlobal, allRedesGlobal, redes.length]);

  const columns: ColumnDef<Estado>[] = [
    { accessorKey: 'nome', header: 'Nome do Estado', cell: ({ row }) => {
      const isAtivo = row.original.ativo;
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          opacity: isAtivo ? 1 : 0.45,
          transition: 'opacity 0.2s ease',
        }}>
          <Globe size={16} style={{ color: isAtivo ? '#6366f1' : '#9ca3af', flexShrink: 0 }} />
          <div>
            <span style={{ fontWeight: 600, color: isAtivo ? 'inherit' : '#64748b' }}>
              {row.original.nome}
            </span>
            <span style={{ marginLeft: 6, fontSize: '.75rem', color: '#9ca3af', fontWeight: 500 }}>
              ({row.original.sigla})
            </span>
          </div>
        </div>
      );
    }},
    { id: 'municipios', header: 'Municípios', cell: ({ row }) => {
      const isAtivo = row.original.ativo;
      const v = row.original._count?.municipios || 0;
      return <span className={v && isAtivo ? 'badge badge-active' : 'badge'} style={v && isAtivo ? undefined : { opacity: .35, color: '#9ca3af' }}>{v}</span>;
    }},
    { accessorKey: 'ativo', header: 'Status', cell: ({ row }) => {
      if (!row.original.ativo) {
        return <span className="badge badge-inactive">Inativo</span>;
      }
      const temMunicipios = (row.original._count?.municipios || 0) > 0;
      if (temMunicipios) {
        return <span className="badge badge-active" title="Estado ativo com municípios vinculados">Ativo</span>;
      }
      return (
        <span
          className="badge"
          style={{ background: 'rgba(245,158,11,0.15)', color: '#d97706', border: '1px solid rgba(245,158,11,0.35)', fontWeight: 700 }}
          title="Estado cadastrado, mas sem municípios vinculados (Pendente de municípios)"
        >
          Pendente
        </span>
      );
    }},
    { id: 'actions', header: '', cell: ({ row }) => (
      <div style={{ display: 'flex', gap: 4 }}>
        <button className="action-btn" onClick={() => openEdit(row.original)} title="Editar"><Pencil size={15} /></button>
        {devSettings.allowDeleteEstados && (
          row.original.ativo ? (
            <button className="action-btn action-btn-danger" onClick={() => requestDelete(row.original)} title="Desativar"><PowerOff size={15} /></button>
          ) : (
            <button className="action-btn" style={{ color: '#10b981' }} onClick={() => handleActivateEstado(row.original)} title="Reativar"><Power size={15} /></button>
          )
        )}
      </div>
    )},
  ];

  const kpis = [
    {
      icon: Globe,
      label: 'Estados',
      value: stats.estadosAtivos,
      subValue: stats.estadosInativos > 0
        ? `${stats.estadosAtivos} ativos • ${stats.estadosInativos} inativo${stats.estadosInativos !== 1 ? 's' : ''}`
        : undefined,
      color: '#6366f1'
    },
    {
      icon: MapPin,
      label: 'Municípios',
      value: stats.municipiosAtivos,
      subValue: (stats.municipiosPendentes > 0 || stats.municipiosInativos > 0)
        ? `${stats.municipiosEmOperacao} em operação • ${stats.municipiosPendentes} pendente${stats.municipiosPendentes !== 1 ? 's' : ''}${stats.municipiosInativos > 0 ? ` • ${stats.municipiosInativos} inativo${stats.municipiosInativos !== 1 ? 's' : ''}` : ''}`
        : `${stats.municipiosAtivos} ativos`,
      color: '#f59e0b'
    },
    {
      icon: Building2,
      label: 'Escolas',
      value: stats.escolasAtivas,
      subValue: (stats.escolasEmImplantacao > 0 || stats.escolasInativas > 0)
        ? `${stats.escolasEmAtividade} em atividade • ${stats.escolasEmImplantacao} em implantação${stats.escolasInativas > 0 ? ` • ${stats.escolasInativas} inativa${stats.escolasInativas !== 1 ? 's' : ''}` : ''}`
        : `${stats.escolasAtivas} ativas`,
      color: '#10b981'
    },
    {
      icon: Network,
      label: 'Redes',
      value: stats.redesAtivas,
      subValue: stats.redesInativas > 0
        ? `${stats.redesAtivas} ativas • ${stats.redesInativas} inativa${stats.redesInativas !== 1 ? 's' : ''}`
        : undefined,
      color: '#8b5cf6'
    },
  ];

  return (
    <div className="admin-page animate-fadeIn">

      {/* ═══ HERO BANNER ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #3730a3 40%, #6366f1 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(99,102,241,.15)' }} />
        <div style={{ position: 'absolute', bottom: -40, left: '30%', width: 160, height: 160, borderRadius: '50%', background: 'rgba(55,48,163,.1)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <Globe size={18} style={{ color: '#c7d2fe' }} />
                <span style={{ color: 'rgba(255,255,255,.4)', fontSize: '.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.12em' }}>Gestão</span>
              </div>
              <h1 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>Estados</h1>
              <p style={{ color: 'rgba(255,255,255,.45)', fontSize: '.82rem', marginTop: 4 }}>
                {stats.estadosAtivos} estado{stats.estadosAtivos !== 1 ? 's' : ''} ativo{stats.estadosAtivos !== 1 ? 's' : ''} {stats.estadosInativos > 0 ? `(${stats.estadosInativos} inativo${stats.estadosInativos !== 1 ? 's' : ''})` : ''} nas redes de ensino
              </p>
            </div>
            <button
              onClick={openNew}
              data-lab-action="novo-estado"
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10,
                background: '#ffffff', color: '#3730a3', fontWeight: 700, fontSize: '.85rem', border: 'none', cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(0,0,0,0.15)', transition: 'all 0.15s ease',
              }}
            >
              <Plus size={16} /> Novo Estado
            </button>
          </div>
        </div>
      </div>

      {/* ═══ KPIS ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
        {kpis.map((k, i) => (
          <div key={i} style={{
            background: 'var(--bg-card, white)', borderRadius: 14, padding: '18px 20px',
            border: '1px solid var(--border, #e5e7eb)', display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10, background: `${k.color}15`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <k.icon size={22} style={{ color: k.color }} />
            </div>
            <div>
              <div style={{ fontSize: '.75rem', color: '#6b7280', fontWeight: 500 }}>{k.label}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-main, #111827)', lineHeight: 1.2 }}>{k.value}</span>
                {k.subValue && (
                  <span style={{ fontSize: '.72rem', color: '#9ca3af', fontWeight: 600 }}>
                    ({k.subValue})
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ═══ TABELAS AGRUPADAS POR REDE ═══ */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Carregando estados...</div>
      ) : Object.keys(groupedByRede).length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Nenhum estado cadastrado.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 32 }}>
          {Object.entries(groupedByRede).map(([redeKey, group]) => {
            const isCollapsed = collapsedRedes.has(redeKey);
            const redeNome = group.rede?.nome || 'Sem Rede Vinculada';
            const ativosNoGrupo = group.items.filter(i => i.ativo).length;
            const inativosNoGrupo = group.items.filter(i => !i.ativo).length;
            return (
              <div key={redeKey} style={{ borderRadius: 12, border: '1px solid var(--border, #e5e7eb)', overflow: 'hidden' }}>
                <div
                  onClick={() => toggleRede(redeKey)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 16px', background: '#111827', border: 'none',
                    cursor: 'pointer', userSelect: 'none',
                  }}
                >
                  <Network size={16} style={{ color: '#818cf8', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontWeight: 700, fontSize: '.9rem', color: 'rgba(255,255,255,.9)', textAlign: 'left' }}>
                    {redeNome}
                  </span>
                  <span style={{
                    fontSize: '.72rem', fontWeight: 600, padding: '2px 8px', borderRadius: 99,
                    background: 'rgba(255,255,255,.1)', color: 'rgba(255,255,255,.6)',
                  }}>
                    {ativosNoGrupo} ativo{ativosNoGrupo !== 1 ? 's' : ''}{inativosNoGrupo > 0 ? ` • ${inativosNoGrupo} inativo` : ''}
                  </span>
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Buscar estado..."
                    onClick={e => e.stopPropagation()}
                    style={{
                      width: 200, padding: '6px 12px', borderRadius: 8,
                      border: '1px solid rgba(255,255,255,.15)', background: 'rgba(255,255,255,.08)',
                      color: 'rgba(255,255,255,.8)', fontSize: '.8rem', outline: 'none',
                    }}
                  />
                </div>
                {!isCollapsed && (
                  <div style={{ padding: '0 0 4px' }}>
                    <DataTable columns={columns} data={group.items} isLoading={false} searchPlaceholder="" showToolbar={false} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal open={modalOpen} onClose={handleCancelClick}
        title={editing ? `Editar: ${editing.nome} (${editing.sigla})` : 'Novo Estado'}
        footer={renderFooter()}>
        <style>{`.modal-content { max-width: 680px !important; }`}</style>
        {editing ? (<>{renderEditTabs()}{renderEditContent()}</>) : renderDadosForm()}
      </Modal>

      {/* Diálogo Institucional de Confirmação para Descarte de Dados */}
      <ConfirmModal
        open={discardConfirmOpen}
        title="Descartar alterações?"
        message="Tem certeza que deseja cancelar o cadastro do estado? Todas as informações preenchidas serão perdidas."
        confirmLabel="Sim, descartar"
        cancelLabel="Continuar preenchendo"
        variant="warning"
        onCancel={() => setDiscardConfirmOpen(false)}
        onConfirm={handleConfirmDiscard}
      />

      <ConfirmModal
        open={!!deleteConfirm}
        title="Desativar estado"
        message={deleteConfirm ? `Desativar o estado "${deleteConfirm.nome}" (${deleteConfirm.sigla})?` : ''}
        confirmLabel="Desativar"
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => void confirmDeleteEstado()}
      />

      {/* Modal de Confirmação com Senha de Segurança para Remoção Permanente de Estado */}
      <PasswordConfirmModal
        open={!!estadoToHardDelete}
        title="Remover Estado Permanentemente"
        description={`Para remover permanentemente o estado "${estadoToHardDelete?.nome}" (${estadoToHardDelete?.sigla}), digite a palavra-chave de autorização:`}
        requiredPassword="marcelo"
        confirmLabel="Remover Estado"
        onCancel={() => setEstadoToHardDelete(null)}
        onConfirm={confirmHardDeleteEstado}
      />
    </div>
  );
}
